import { EmailHistory } from '../../email/EmailHistory';
import { EmailSend } from '../../email/EmailSend';
import BlockRounded from '@mui/icons-material/BlockRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import ReceiptRounded from '@mui/icons-material/ReceiptRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import { Alert, Box, Button, Card, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { Money } from '../../../components/Money';
import { businessDate, dateTime, money } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import {
  DetailHeader,
  DetailSkeleton,
  ErrorState,
  InfoRow,
  LinesTable,
  ReasonDialog,
  SummaryCard,
  SummaryGrid,
  TotalsBlock,
} from '../components/DocParts';
import { afterSaleKeys, idem, salesKeys } from '../lib/api';
import { dec } from '../lib/decimal';
import { invoiceStatus, plural } from '../lib/documents';
import { invoiceDoc, receiptData, shopHeader } from '../print/builders';
import { A4Document, Receipt80 } from '../print/documents';
import { usePrint } from '../print/usePrint';
import { WhatsappHistory, WhatsappSend } from '../../whatsapp';
import { AttachmentsPanel } from '../../attachments';
import { useShopLogo } from '../../../lib/images';
import { MoreMenu } from '../../../components/MoreMenu';
import { useLinked, useLinkedOne } from '../../../components/useLinked';
import { useLinkedPayments } from '../../../components/useLinkedPayments';

export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const { can, me } = useAuth();
  const { settings, format } = useShop();
  const logo = useShopLogo();
  const notify = useNotify();
  const qc = useQueryClient();
  const { print, element } = usePrint();
  const [cancelOpen, setCancelOpen] = useState(false);
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: [...salesKeys.invoices, id],
    queryFn: () => unwrap(api.GET('/sales-invoices/{id}', { params: { path: { id } } })),
  });

  const order = useLinkedOne('order', query.data?.salesOrderId);
  // A counter sale's own delivery; an order's invoice bills the order's deliveries.
  const ownDelivery = useLinkedOne('delivery', query.data?.deliveryId);
  const orderDeliveries = useLinked(
    'deliveries',
    query.data?.salesOrderId && !query.data.deliveryId
      ? { salesOrderId: query.data.salesOrderId }
      : null,
  );
  const delivery = query.data?.deliveryId ? ownDelivery : orderDeliveries;
  const payments = useLinkedPayments({ salesInvoiceId: id });
  const creditNotes = useLinked('creditNotes', { salesInvoiceId: id });
  const returns = useLinked('salesReturns', { salesInvoiceId: id });

  const cancel = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/sales-invoices/{id}/cancel', {
          params: { path: { id }, header: idem() },
          body: { version: query.data?.version ?? 1, reason },
        }),
      ),
    onSuccess: async (inv) => {
      qc.setQueryData([...salesKeys.invoices, id], inv);
      notify.success(`Invoice ${inv.number} cancelled`);
      await Promise.all(afterSaleKeys.map((k) => qc.invalidateQueries({ queryKey: [...k] })));
    },
  });

  if (query.isLoading) return <DetailSkeleton />;
  if (query.isError || !query.data)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const inv = query.data;
  const chip = invoiceStatus(inv);
  const cancelled = inv.status === 'CANCELLED';
  const shop = shopHeader(settings, undefined, logo);
  const paid = dec(inv.amountPaid) > 0n;
  const paidPct = dec(inv.total) > 0n ? Number((dec(inv.amountPaid) * 100n) / dec(inv.total)) : 0;

  return (
    <Box>
      <DetailHeader
        back={{ to: '/sales/invoices', label: 'Invoices' }}
        title={inv.number}
        status={chip}
        subtitle={
          <>
            {inv.contact.name} · {businessDate(inv.docDate, format)}
            {inv.postedAt && (
              <Typography component="span" variant="body2" color="text.disabled">
                {' '}
                · posted {dateTime(inv.postedAt, format)}
              </Typography>
            )}
          </>
        }
        actions={
          <>
            {!cancelled && dec(inv.outstanding) > 0n && can('payments.create') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentsRounded />}
                component={RouterLink}
                to={`/payments/new?purpose=RECEIPT&contactId=${inv.contact.id}&invoiceId=${inv.id}`}
              >
                Receive payment
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ReceiptRounded />}
              onClick={() =>
                print(
                  '80mm',
                  <Receipt80
                    shop={shop}
                    data={receiptData({ invoice: inv, payment: null, changeDue: '0' }, format, {
                      cashier: me?.user.name,
                    })}
                  />,
                )
              }
            >
              Receipt
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintRounded />}
              onClick={() =>
                print(
                  'a4',
                  <A4Document shop={shop} doc={invoiceDoc(inv, format, !!settings?.taxEnabled)} />,
                )
              }
            >
              Print
            </Button>
            <WhatsappSend
              target={{
                documentType: 'sales_invoice',
                documentId: inv.id,
                documentRef: inv.number,
                contact: inv.contact,
                vars: {
                  number: inv.number,
                  name: inv.contact.name,
                  amount: money(inv.total, format),
                  date: businessDate(inv.docDate, format),
                },
                pdf: {
                  fileName: `${inv.number}.pdf`,
                  paper: 'a4',
                  render: () => (
                    <A4Document shop={shop} doc={invoiceDoc(inv, format, !!settings?.taxEnabled)} />
                  ),
                },
              }}
            />
            <EmailSend
              target={{
                documentType: 'sales_invoice',
                documentId: inv.id,
                documentRef: inv.number,
                contact: inv.contact,
                vars: {
                  number: inv.number,
                  name: inv.contact.name,
                  amount: money(inv.total, format),
                  date: businessDate(inv.docDate, format),
                },
                pdf: {
                  fileName: `${inv.number}.pdf`,
                  paper: 'a4',
                  render: () => (
                    <A4Document shop={shop} doc={invoiceDoc(inv, format, !!settings?.taxEnabled)} />
                  ),
                },
              }}
            />
            {!cancelled && can('sales.cancel') && (
              <Tooltip
                title={paid ? 'Payments are allocated to this invoice — reallocate them first' : ''}
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<BlockRounded />}
                    disabled={paid}
                    onClick={() => setCancelOpen(true)}
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
            )}
            <MoreMenu
              linked={[order, delivery, payments, returns, creditNotes]}
              onDuplicate={
                can('sales.create')
                  ? () => void navigate('/sales/invoices/new', { state: { copy: inv } })
                  : undefined
              }
              exportModel="sales_invoice"
              exportId={inv.id}
            />
          </>
        }
      />

      {cancelled && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Cancelled {inv.cancelledAt ? dateTime(inv.cancelledAt, format) : ''}
          {inv.cancelReason ? ` — “${inv.cancelReason}”` : ''}. The sale was reversed in the
          accounts;
          {inv.deliveryId
            ? ' reverse its delivery separately to return the stock.'
            : ' stock is unchanged.'}
        </Alert>
      )}

      <SummaryGrid>
        <SummaryCard
          label="Total"
          value={<Money value={inv.total} />}
          hint={plural(inv.lines.length, 'line')}
        />
        <SummaryCard
          label="Paid"
          value={<Money value={inv.amountPaid} />}
          tone={dec(inv.amountPaid) > 0n && !cancelled ? 'success' : undefined}
          hint={cancelled ? '—' : `${String(Math.min(100, paidPct))}% of the total`}
        />
        <SummaryCard
          label="Balance due"
          value={<Money value={cancelled ? '0' : inv.outstanding} />}
          tone={
            !cancelled && dec(inv.outstanding) > 0n
              ? inv.overdue
                ? 'error'
                : 'warning'
              : undefined
          }
          hint={!cancelled && inv.overdue ? 'Past the due date' : undefined}
        />
        <SummaryCard
          label="Due date"
          value={businessDate(inv.dueDate, format)}
          tone={inv.overdue && !cancelled ? 'error' : undefined}
          hint={inv.reference ? `Ref. ${inv.reference}` : undefined}
        />
      </SummaryGrid>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 320px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Card>
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography variant="h5">Items</Typography>
          </Box>
          <LinesTable lines={inv.lines} />
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <TotalsBlock
              doc={inv}
              extra={
                !cancelled && (
                  <Stack
                    direction="row"
                    sx={(t) => {
                      const c = (t.vars ?? t).palette.primary.main;
                      return {
                        justifyContent: 'space-between',
                        mt: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: '10px',
                        bgcolor: `color-mix(in srgb, ${c} 8%, transparent)`,
                        fontWeight: 700,
                      };
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>Balance due</Typography>
                    <Typography className="tabular" sx={{ fontWeight: 700 }}>
                      <Money value={inv.outstanding} />
                    </Typography>
                  </Stack>
                )
              }
            />
          </Box>
        </Card>

        <Stack spacing={3}>
          <Card sx={{ p: 2.5 }}>
            <Typography variant="overline" color="text.secondary">
              Customer
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>
              {inv.contact.name}
            </Typography>
            {inv.contact.phone && (
              <Typography color="text.secondary">{inv.contact.phone}</Typography>
            )}
            <Button
              component={RouterLink}
              to={`/customers/${inv.contact.id}`}
              size="small"
              sx={{ mt: 1, ml: -1 }}
            >
              View customer
            </Button>
          </Card>
          <Card sx={{ p: 2.5 }}>
            <Typography variant="overline" color="text.secondary">
              Details
            </Typography>
            <InfoRow label="Invoice date">{businessDate(inv.docDate, format)}</InfoRow>
            <InfoRow label="Prices">{inv.pricesIncludeTax ? 'Include tax' : 'Before tax'}</InfoRow>
            {inv.salesOrderId && (
              <InfoRow label="Order">
                <Link component={RouterLink} to={`/sales/orders/${inv.salesOrderId}`}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <ShoppingBagRounded sx={{ fontSize: 16 }} />
                    <span>Open order</span>
                  </Stack>
                </Link>
              </InfoRow>
            )}
            {inv.deliveryId && (
              <InfoRow label="Delivery">
                <Link component={RouterLink} to={`/sales/deliveries/${inv.deliveryId}`}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <LocalShippingRounded sx={{ fontSize: 16 }} />
                    <span>Open delivery</span>
                  </Stack>
                </Link>
              </InfoRow>
            )}
            {inv.notes && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {inv.notes}
                </Typography>
              </Box>
            )}
          </Card>
          <AttachmentsPanel recordType="SALES_INVOICE" recordId={inv.id} />
          <WhatsappHistory documentType="sales_invoice" documentId={inv.id} />
          <EmailHistory documentType="sales_invoice" documentId={inv.id} />
        </Stack>
      </Box>

      <ReasonDialog
        open={cancelOpen}
        title={`Cancel ${inv.number}?`}
        body={
          <>
            This posts an exact reversing entry and removes <Money value={inv.total} /> from{' '}
            {inv.contact.name}’s balance.
            {inv.deliveryId && ' The delivery stays — reverse it separately to return the stock.'}
          </>
        }
        confirmLabel="Cancel invoice"
        onConfirm={(reason) => cancel.mutateAsync(reason)}
        onClose={() => setCancelOpen(false)}
      />
      {element}
    </Box>
  );
}
